import {
  Box,
  Button,
  HStack,
  Heading,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useState } from "react"
import type { ReactNode } from "react"
import type { IconType } from "react-icons"
import {
  FaBolt,
  FaChevronDown,
  FaChevronUp,
  FaFire,
  FaLayerGroup,
  FaPlay,
} from "react-icons/fa"

interface StudySessionHeroProps {
  modeLabel: string
  totalCards: number
  currentCard: number
  xpEarned: number
  streak: number
  progressPct: number
  progressTrack: string
  deckName?: string
  actions?: ReactNode
  compactActions?: ReactNode
}

export function StudySessionHero({
  modeLabel,
  totalCards,
  currentCard,
  xpEarned,
  streak,
  progressPct,
  progressTrack,
  deckName,
  actions,
  compactActions,
}: StudySessionHeroProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia("(max-width: 767px)").matches
  })

  return (
    <Box
      as="aside"
      aria-label="Study session summary"
      overflow="hidden"
      borderWidth={1}
      borderColor="teal.200"
      borderRadius="xl"
      bg="white"
      _dark={{ bg: "gray.900", borderColor: "teal.700" }}
      p={{ base: 4, md: 5 }}
      boxShadow="0 16px 40px rgba(15, 118, 110, 0.1)"
      alignSelf="start"
      position={{ md: "sticky" }}
      top={{ md: 4 }}
    >
      <VStack align="stretch" gap={isCollapsed ? 0 : 4}>
        <HStack gap={3} align="center" minH="36px">
          <Box
            boxSize={isCollapsed ? "32px" : "40px"}
            borderRadius="full"
            bg="teal.500"
            color="white"
            display="grid"
            placeItems="center"
            boxShadow="0 12px 24px rgba(13, 148, 136, 0.24)"
            flexShrink={0}
          >
            <Icon as={FaPlay} boxSize={4} />
          </Box>
          <Box minW={0} flex="1" overflow="hidden">
            {isCollapsed ? (
              <HStack gap={2} minW={0} wrap="nowrap" overflow="hidden">
                <Heading size="sm" flexShrink={1} minW={0} lineClamp={1}>
                  {modeLabel}
                </Heading>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  _dark={{ color: "gray.400" }}
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  {currentCard}/{totalCards} cards
                </Text>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="yellow.600"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  +{xpEarned} XP
                </Text>
                {streak > 0 && (
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="orange.500"
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    {streak} streak
                  </Text>
                )}
              </HStack>
            ) : (
              <>
                <Text
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="teal.600"
                  _dark={{ color: "teal.300" }}
                  fontWeight="bold"
                  mb={1}
                >
                  Review arena
                </Text>
                <Heading size="md">{modeLabel}</Heading>
                {deckName && (
                  <Text
                    color="gray.600"
                    _dark={{ color: "gray.300" }}
                    fontSize="sm"
                    lineClamp={2}
                  >
                    {deckName}
                  </Text>
                )}
              </>
            )}
          </Box>

          <HStack gap={2} flexShrink={0}>
            {isCollapsed && compactActions}
            <Button
              aria-label={
                isCollapsed ? "Expand study summary" : "Collapse study summary"
              }
              aria-expanded={!isCollapsed}
              size="xs"
              variant="ghost"
              minW="32px"
              px={2}
              onClick={() => setIsCollapsed((value) => !value)}
            >
              <Icon as={isCollapsed ? FaChevronDown : FaChevronUp} />
            </Button>
          </HStack>
        </HStack>

        {!isCollapsed && (
          <>
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  _dark={{ color: "gray.400" }}
                >
                  Card {currentCard} of {totalCards}
                </Text>
                <Text fontSize="xs" fontWeight="bold" color="teal.600">
                  {progressPct}%
                </Text>
              </HStack>
              <Box bg={progressTrack} borderRadius="full" h="8px">
                <Box
                  bg="teal.500"
                  borderRadius="full"
                  h="100%"
                  w={`${progressPct}%`}
                  transition="width 0.4s ease"
                />
              </Box>
            </Box>

            <VStack align="stretch" gap={2}>
              <SessionHeroStat
                icon={FaLayerGroup}
                label="Cards"
                value={`${currentCard}/${totalCards}`}
                color="teal.500"
              />
              <SessionHeroStat
                icon={FaBolt}
                label="XP earned"
                value={`+${xpEarned}`}
                color="yellow.500"
              />
              <SessionHeroStat
                icon={FaFire}
                label="Streak"
                value={String(streak)}
                color="orange.500"
              />
            </VStack>

            {actions && <HStack gap={2}>{actions}</HStack>}
          </>
        )}
      </VStack>
    </Box>
  )
}

function SessionHeroStat({
  icon,
  label,
  value,
  color,
}: {
  icon: IconType
  label: string
  value: string
  color: string
}) {
  return (
    <Box
      borderWidth={1}
      borderColor="gray.200"
      _dark={{ borderColor: "gray.700", bg: "gray.800" }}
      borderRadius="md"
      bg="gray.50"
      p={3}
    >
      <HStack gap={3}>
        <Box
          boxSize="32px"
          borderRadius="full"
          bg="white"
          _dark={{ bg: "gray.950" }}
          color={color}
          display="grid"
          placeItems="center"
          flexShrink={0}
        >
          <Icon as={icon} />
        </Box>
        <Box>
          <Text fontSize="lg" fontWeight="bold" lineHeight={1}>
            {value}
          </Text>
          <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>
            {label}
          </Text>
        </Box>
      </HStack>
    </Box>
  )
}
